import { memo, useRef, useCallback, useEffect } from 'react';
import { getNormalizedValueFromPercentage } from '@dtvgo/utils';
import { SliderProvider } from './SliderContext.js';
import { isLeftButton, isLeftButtonMove, SliderClassNames, stopEventPropagation } from './utils.js';
import './Slider.scss';
import { jsx } from 'react/jsx-runtime';
import '@dtvgo/classnames';

/* External */
function Slider(_ref) {
  var className = _ref.className,
    value = _ref.value,
    minValue = _ref.minValue,
    maxValue = _ref.maxValue,
    flat = _ref.flat,
    vertical = _ref.vertical,
    dragFromOutside = _ref.dragFromOutside,
    stopClickPropagation = _ref.stopClickPropagation,
    focused = _ref.focused,
    onSetValue = _ref.onSetValue,
    onMouseOver = _ref.onMouseOver,
    onDragStart = _ref.onDragStart,
    onDragEnd = _ref.onDragEnd,
    onMouseLeave = _ref.onMouseLeave,
    onKeyDown = _ref.onKeyDown,
    children = _ref.children;
  var element = useRef(null);
  var isMouseDown = useRef(false);
  var isInteractive = !!onSetValue;
  var getPercentageFromMouseEvent = useCallback(function (event) {
    var _element$current$getB = element.current.getBoundingClientRect(),
      height = _element$current$getB.height,
      width = _element$current$getB.width,
      left = _element$current$getB.left,
      top = _element$current$getB.top;
    var positionX = event.clientX - left;
    var positionY = event.clientY - top;
    var position = vertical ? height - positionY : positionX;
    var reference = vertical ? height : width;
    return position / reference;
  }, [vertical]);
  var getValueFromMouseEvent = useCallback(function (event) {
    var percentage = getPercentageFromMouseEvent(event);
    return getNormalizedValueFromPercentage(percentage, maxValue, minValue);
  }, [getPercentageFromMouseEvent, maxValue, minValue]);
  var handleMouseDown = useCallback(function (event) {
    var valueEvent = getValueFromMouseEvent(event);
    if (isLeftButton(event)) {
      if (onDragStart) {
        onDragStart(valueEvent);
      }
      isMouseDown.current = true;
      if (onSetValue) {
        onSetValue(valueEvent);
      }
    }
  }, [getValueFromMouseEvent, onSetValue, onDragStart]);
  var handleMouseMove = useCallback(function (event) {
    var valueEvent = getValueFromMouseEvent(event);
    if (onMouseOver) {
      onMouseOver(valueEvent);
    }
    if (isInteractive) {
      if (!isMouseDown.current && dragFromOutside && isLeftButtonMove(event)) {
        onDragStart(valueEvent);
        isMouseDown.current = true;
      }
      if (isMouseDown.current) {
        onSetValue(valueEvent);
      }
    }
  }, [getValueFromMouseEvent, onMouseOver, isInteractive, dragFromOutside, onDragStart, onSetValue]);
  var finishDrag = useCallback(function (event) {
    var valueEvent = getValueFromMouseEvent(event);
    if (isMouseDown.current) {
      if (onDragEnd) {
        onDragEnd(valueEvent);
      }
      isMouseDown.current = false;
      if (onSetValue) {
        onSetValue(valueEvent);
      }
    }
  }, [getValueFromMouseEvent, onDragEnd, onSetValue]);
  var handleMouseLeave = useCallback(function (event) {
    finishDrag(event);
    if (onMouseLeave) onMouseLeave(event);
  }, [finishDrag, onMouseLeave]);

  /* When focused prop became true, add event listener */
  useEffect(function () {
    if (focused && onKeyDown) {
      document.addEventListener('keydown', onKeyDown);
    }
    return function () {
      if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
    };
  }, [focused, onKeyDown]);
  return (
    /*#__PURE__*/
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events -- key down events are handled
        by manually adding a listener to document object when focused prop is true */
    jsx("div", {
      className: SliderClassNames.block({
        vertical: vertical,
        interactive: isInteractive,
        flat: flat
      }, className),
      role: "slider",
      "aria-valuenow": value,
      "aria-valuemin": minValue,
      "aria-valuemax": maxValue,
      "aria-orientation": vertical ? 'vertical' : 'horizontal',
      onMouseLeave: isInteractive ? handleMouseLeave : onMouseLeave,
      onMouseDown: isInteractive ? handleMouseDown : undefined,
      onMouseMove: isInteractive || onMouseOver ? handleMouseMove : undefined,
      onMouseUp: isInteractive ? finishDrag : undefined,
      onClick: stopClickPropagation ? stopEventPropagation : undefined,
      tabIndex: "-1",
      ref: element,
      children: /*#__PURE__*/jsx("div", {
        className: SliderClassNames.element('holder'),
        children: /*#__PURE__*/jsx(SliderProvider, {
          vertical: vertical,
          flat: flat,
          minValue: minValue,
          maxValue: maxValue,
          value: value,
          children: children
        })
      })
    })
  );
}
Slider.defaultProps = {
  className: '',
  value: 0,
  minValue: 0,
  maxValue: 1,
  vertical: false,
  flat: false,
  dragFromOutside: false,
  stopClickPropagation: false,
  focused: false,
  children: null,
  onSetValue: undefined,
  onMouseLeave: undefined,
  onMouseOver: undefined,
  onDragStart: undefined,
  onDragEnd: undefined,
  onKeyDown: undefined
};
var Slider$1 = /*#__PURE__*/memo(Slider);

export { Slider$1 as default };
//# sourceMappingURL=Slider.js.map
